import api from './api';

/**
 * Personal asset library — user-scoped media generated through the Media Lab.
 * Separate from project-scoped media (which lives on a WorkflowProject).
 */
const assetsService = {
    list: async (params = {}) => {
        const response = await api.get('/assets', { params });
        return response.data;
    },

    create: async (data) => {
        const response = await api.post('/assets', data);
        return response.data;
    },

    update: async (id, data) => {
        const response = await api.patch(`/assets/${id}`, data);
        return response.data;
    },

    /** Soft-delete (default) → moves to trash, auto-purged after 48h.
     *  Pass `permanent: true` to hard-delete immediately (used by trash
     *  view's "Delete forever" action). */
    delete: async (id, { permanent = false } = {}) => {
        const response = await api.delete(`/assets/${id}`, {
            params: permanent ? { permanent: 'true' } : {},
        });
        return response.data;
    },

    /** Restore a soft-deleted asset out of trash. 404s if the TTL already
     *  hard-purged it (>48h after deletedAt). */
    restore: async (id) => {
        const response = await api.post(`/assets/${id}/restore`);
        return response.data;
    },

    /** Server-side liveness check for an array of URLs. Returns
     *  [{ url, alive }] — used by the Reuse flow to drop dead reference
     *  images without hitting CORS errors in the browser console. */
    checkUrls: async (urls) => {
        const response = await api.post('/assets/check-urls', { urls });
        return response.data;
    },

    backfillFromGenerations: async () => {
        const response = await api.post('/assets/backfill-from-generations');
        return response.data;
    },

    /** Bulk-import an array of media items (from localStorage projectMedia,
     *  external sources, etc.) into the Asset library. Skips items whose
     *  URL already exists. Items: [{ type, url, projectId?, prompt?, ... }] */
    importBulk: async (items) => {
        const response = await api.post('/assets/import', { items });
        return response.data;
    },

    /** Wipe all the user's Asset rows and rebuild them from authoritative
     *  sources (Generation table). Use when the library has accumulated
     *  noise that doesn't dedupe cleanly. Loses upload-only Asset rows;
     *  the underlying R2 files are untouched, the user just re-uploads. */
    rebuild: async () => {
        const response = await api.post('/assets/rebuild');
        return response.data;
    },

    /** Physically remove duplicate Asset records. Idempotent.
     *  Strategies: URL match → generationId match → content fingerprint.
     *  Pass `aggressive: true` to collapse same-prompt re-rolls regardless
     *  of when they were generated (kills variants, but matches the user's
     *  expectation that re-generations of the same prompt = "duplicate"). */
    dedupe: async ({ aggressive = false } = {}) => {
        // Body must be a JSON object (or string) — body-parser rejects bare
        // `null` with "Unexpected token 'n'". Empty object satisfies it.
        const response = await api.post('/assets/dedupe', {}, {
            params: aggressive ? { aggressive: 'true' } : {},
        });
        return response.data;
    },
};

export default assetsService;
